<template lang="pug">
v-row( no-gutters, justify="end")
  v-col(cols="12", sm="10", offset-sm="2")
    v-expansion-panels.suus__comment-list(accordion, flat, :value="0")
      v-expansion-panel
        v-expansion-panel-header.text-right.pa-0
          span(:style="commentCountStyles") {{ comments.length }} {{ commentsCountText }}
          template(v-slot:actions)
            v-icon(color="textColor") {{ panelIcon }}
        v-expansion-panel-content.pa-0
          OmissionLoader(
            ref="omissionLoader",
            :load-more="loadMore"
            :total-count="comments.length"
            :page-size="3"
            :initial-leading-count="2"
            :initial-trailing-count="0"
            @totalLoaded="$emit('totalLoaded', $event)"
          )
            template(v-slot:item="{ item: comment }")
              Comment(
                :comment="comment"
                :viewContext="viewContext"
                @delete="deleteComment(comment)"
                @save="saveComment"
              )

            template(v-slot:loading)
              CommentLoading

            template(v-slot:loadMore="{ loadMore }")
              TextButton(label="Ältere Kommentare laden" variant="small" @click="loadMore()")
</template>

<script lang="ts">
import {
  ref,
  computed,
  defineComponent,
  PropType,
  watch,
  Ref,
  reactive,
} from "vue"
import THEME from "../../constants/theme"
import { Comment as IComment } from "suus-api"

import TextButton from "../UI/TextButton.vue"
import Comment from "./Comment.vue"
import { mdiChevronDown } from "@mdi/js"
import { ViewContext } from "concerns/useContextName"
import OmissionLoader from "features/omission-loader/OmissionLoader.vue"
import { LoadMoreFunction } from "features/omission-loader/use-omission-loader-internal"
import CommentLoading from "components/Comment/CommentLoading.vue"

export type UpdateCommentFunction = (comment: IComment) => Promise<IComment>
export type DeleteCommentFunction = (comment: IComment) => Promise<number>

export default defineComponent({
  name: "CommentList",
  components: { CommentLoading, OmissionLoader, Comment, TextButton },
  props: {
    viewContext: { type: Object as PropType<ViewContext> },
    comments: {
      type: Array as () => IComment[],
      default: () => [],
    },
    loading: { type: Boolean, default: false },
    updateComment: { type: Function as PropType<UpdateCommentFunction> },
    deleteComment: { type: Function as PropType<DeleteCommentFunction> },
  },
  setup(props) {
    const omissionLoader = ref<InstanceType<typeof OmissionLoader>>(null)
    const comments = ref<IComment[]>(props.comments)

    watch(props, (props) => (comments.value = props.comments))

    async function deleteComment(comment: IComment): Promise<void> {
      const deletedCommentId = await props.deleteComment(comment)
      omissionLoader.value?.removeItem(deletedCommentId)
    }

    const updateComment = async (comment: IComment) => {
      const updatedComment = await props.updateComment(comment)
      omissionLoader.value?.updateItem(comment.id, () => updatedComment)
    }

    const commentsCountText = computed(() => {
      return comments.value.length === 1 ? "Kommentar" : "Kommentare"
    })
    const { hyperlink, rootFontSize } = THEME.typography
    const { textColor } = THEME.colors.light

    const loadMore: LoadMoreFunction = (offset, pageSize) => {
      const start = offset
      const index = offset + pageSize
      const cappedIndex =
        index > comments.value.length ? comments.value.length : index
      return Promise.resolve(comments.value.slice(start, cappedIndex))
    }

    const add = (comment: IComment) => {
      omissionLoader.value?.addItemToLeading(comment)
      comments.value.unshift(comment)
    }

    return {
      omissionLoader,
      comments,
      saveComment: updateComment,
      deleteComment,
      commentCountStyles: {
        ...hyperlink,
        fontSize: `${rootFontSize * 0.8}px`,
        lineHeight: `${rootFontSize * 0.8 * 1.5}px`,
        color: textColor,
      },
      commentsCountText,
      panelIcon: mdiChevronDown,
      loadMore,
      add,
    }
  },
})
</script>

<style lang="sass" scoped>
.suus__comment-list::v-deep .v-expansion-panel
  background-color: transparent
.suus__comment-list::v-deep .v-expansion-panel-content__wrap
  padding: 0 !important
.suus__comment-list::v-deep  .v-expansion-panel--active>.v-expansion-panel-header
  min-height: 40px
</style>
